import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CustomerService } from '../customer-service/customer.service';
import { RoleService } from '../role-service/role.service';

export type SortDirection = 'asc' | 'desc';

export interface UserListSort {
  field: string;
  direction: SortDirection;
}

export interface UserListFilter {
  field: string;
  operator: string;
  value: string | number | boolean | string[] | null;
}

export interface UserListSearchPayload {
  page: number;
  size: number;
  sorts: UserListSort[];
  filters: UserListFilter[];
}

/** POST /userservice/api/user-management */
export interface CreateUserApiPayload {
  email: string;
  firstName: string;
  lastName: string;
  /** Primary/first customer — kept for backward-compatible API clients. */
  customerAssignmentId: number | string;
  /** All customers the user should be assigned to. */
  customerAssignmentIds: Array<number | string>;
  enableAdminAccess: boolean;
  roleIds: number[];
  status?: 'Active' | 'Inactive' | string;
}

export interface UpdateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  emailId?: string;
  enableAdminAccess: boolean;
  /** Primary customer used for query-param context. */
  customerId: number;
  customerAssignmentId: number;
  customerAssignmentIds: number[];
  roleIds: number[];
  status: 'Active' | 'Inactive' | string;
}

export interface UserListRow {
  userId: number;
  username: string;
  email: string;
  customerName: string;
  customerNames?: string[];
  customerAssignmentId?: number | string;
  customerAssignmentIds?: Array<number | string>;
  userRole: string;
  userRoles: string[];
  status: 'Active' | 'Inactive' | string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  customerId?: number | string;
  roleIds?: number[];
  enableAdminAccess?: boolean;
}

export interface SpringPage<T> {
  content: T[];
  totalElements: number;
}

interface UserSearchRawRow {
  userId?: number;
  id?: number;
  firstName?: string;
  firstname?: string;
  lastName?: string;
  lastname?: string;
  username?: string;
  userName?: string;
  email?: string;
  emailId?: string;
  companyName?: string;
  customerName?: string;
  customerId?: number | string;
  customerAssignment?: number | string;
  customerAssignmentId?: number | string;
  customerAssignmentIds?: Array<number | string>;
  customerNames?: string[];
  userRole?: string;
  roleName?: string;
  roleIds?: number[];
  roles?: Array<{
    roleId?: number;
    id?: number;
    roleName?: string;
    name?: string;
    roleCode?: string;
  }>;
  userRoles?: Array<string | Record<string, unknown>>;
  roleNames?: string[];
  roleList?: Array<Record<string, unknown>>;
  assignedRoles?: Array<Record<string, unknown>>;
  userRoleList?: Array<Record<string, unknown>>;
  roleAssignments?: Array<Record<string, unknown>>;
  enableAdminAccess?: boolean;
  isAdmin?: boolean;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly roleService = inject(RoleService);
  private readonly customerService = inject(CustomerService);
  private readonly baseUrl = `${environment.userServiceUrl}/user-management`;
  private roleNameById = new Map<number, string>();
  private customerNameById = new Map<number, string>();
  private roleCatalog$?: Observable<Map<number, string>>;
  private customerCatalog$?: Observable<Map<number, string>>;

  /** Load role id → name map once (shared by grid + modals). */
  ensureRoleCatalogLoaded(): Observable<Map<number, string>> {
    return this.getRoleCatalog$();
  }

  /** Load customer id → name map once (user list Customer Name column). */
  ensureCustomerCatalogLoaded(): Observable<Map<number, string>> {
    return this.getCustomerCatalog$();
  }

  searchUsers(payload: UserListSearchPayload): Observable<SpringPage<UserListRow>> {
    return forkJoin({
      roleMap: this.getRoleCatalog$(),
      customerMap: this.getCustomerCatalog$(),
    }).pipe(
      switchMap(({ roleMap, customerMap }) =>
        this.http
          .post<SpringPage<UserSearchRawRow> | { data?: SpringPage<UserSearchRawRow> }>(
            `${this.baseUrl}/search`,
            payload
          )
          .pipe(
            map((response) => {
              const page =
                (response as { data?: SpringPage<UserSearchRawRow> })?.data ??
                (response as SpringPage<UserSearchRawRow>);
              return {
                content: (page?.content ?? []).map((row) =>
                  this.normalizeUserRow(
                    this.unwrapSearchRow(row),
                    roleMap,
                    customerMap
                  )
                ),
                totalElements: page?.totalElements ?? 0,
              };
            })
          )
      )
    );
  }

  private getRoleCatalog$(): Observable<Map<number, string>> {
    if (!this.roleCatalog$) {
      this.roleCatalog$ = this.roleService
        .searchRoles({
          page: 0,
          size: 500,
          sorts: [{ field: 'roleName', direction: 'asc' }],
          filters: [{ field: '', operator: '', value: '' }],
        })
        .pipe(
          tap((page) => {
            for (const role of page?.content ?? []) {
              const record = role as unknown as Record<string, unknown>;
              const id = Number(record['roleId'] ?? record['id']);
              const name = String(record['roleName'] ?? record['name'] ?? '').trim();
              if (Number.isFinite(id) && id > 0 && name) {
                this.roleNameById.set(id, name);
              }
            }
          }),
          map(() => this.roleNameById),
          shareReplay(1)
        );
    }
    return this.roleCatalog$;
  }

  private getCustomerCatalog$(): Observable<Map<number, string>> {
    if (!this.customerCatalog$) {
      this.customerCatalog$ = this.customerService.getCustomersV1(0, 500).pipe(
        tap((customers) => {
          for (const customer of customers) {
            const id = Number(customer.id);
            const name = String(customer.customerName ?? '').trim();
            if (Number.isFinite(id) && id > 0 && name) {
              this.customerNameById.set(id, name);
            }
            const legacyId = Number(customer.customerId);
            if (Number.isFinite(legacyId) && legacyId > 0 && name) {
              this.customerNameById.set(legacyId, name);
            }
          }
        }),
        map(() => this.customerNameById),
        shareReplay(1)
      );
    }
    return this.customerCatalog$;
  }

  createUser(payload: CreateUserApiPayload): Observable<unknown> {
    return this.http.post(this.baseUrl, {
      ...payload,
      status: payload.status ?? 'Active',
    });
  }

  getUserById(
    userId: number | string,
    customerId: number | string
  ): Observable<UserListRow> {
    return forkJoin({
      roleMap: this.getRoleCatalog$(),
      customerMap: this.getCustomerCatalog$(),
    }).pipe(
      switchMap(({ roleMap, customerMap }) =>
        this.http
          .get<UserSearchRawRow | { data?: UserSearchRawRow }>(
            `${this.baseUrl}/${userId}?customerId=${customerId}`
          )
          .pipe(
            map((response) => {
              const raw =
                (response as { data?: UserSearchRawRow })?.data ??
                (response as UserSearchRawRow);
              return this.normalizeUserRow(
                this.unwrapSearchRow(raw),
                roleMap,
                customerMap
              );
            })
          )
      )
    );
  }

  updateUser(
    userId: number | string,
    payload: UpdateUserPayload
  ): Observable<unknown> {
    const customerId = payload.customerId;
    const body = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      emailId: payload.emailId ?? payload.email,
      enableAdminAccess: payload.enableAdminAccess,
      customerId,
      customerAssignmentId: payload.customerAssignmentId,
      customerAssignmentIds: payload.customerAssignmentIds,
      roleIds: payload.roleIds,
      status: payload.status,
    };
    return this.http.put(
      `${this.baseUrl}/${userId}?customerId=${customerId}`,
      body
    );
  }

  /** DELETE /userservice/api/user-management/{userId}?customerId={customerId} */
  deleteUser(
    userId: number | string,
    customerId: number | string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${userId}?customerId=${customerId}`
    );
  }

  private unwrapSearchRow(raw: UserSearchRawRow): UserSearchRawRow {
    const record = raw as Record<string, unknown>;
    const nestedUser = record['user'];
    if (nestedUser && typeof nestedUser === 'object') {
      const user = nestedUser as Record<string, unknown>;
      return {
        ...(user as UserSearchRawRow),
        roles:
          (record['roles'] as UserSearchRawRow['roles']) ??
          (user['roles'] as UserSearchRawRow['roles']),
        roleIds:
          (record['roleIds'] as number[]) ?? (user['roleIds'] as number[]),
        roleNames:
          (record['roleNames'] as string[]) ?? (user['roleNames'] as string[]),
        userRoles:
          (record['userRoles'] as UserSearchRawRow['userRoles']) ??
          (user['userRoles'] as UserSearchRawRow['userRoles']),
        customerAssignmentId:
          (record['customerAssignmentId'] as number | string) ??
          (user['customerAssignmentId'] as number | string),
        customerAssignmentIds:
          (record['customerAssignmentIds'] as Array<number | string>) ??
          (user['customerAssignmentIds'] as Array<number | string>),
        customerAssignment:
          (record['customerAssignment'] as number | string) ??
          (user['customerAssignment'] as number | string),
        customerName:
          (record['customerName'] as string) ?? (user['customerName'] as string),
        customerNames:
          (record['customerNames'] as string[]) ??
          (user['customerNames'] as string[]),
      };
    }
    return raw;
  }

  private normalizeUserRow(
    row: UserSearchRawRow,
    roleMap: Map<number, string> = this.roleNameById,
    customerMap: Map<number, string> = this.customerNameById
  ): UserListRow {
    const status = this.resolveStatus(row);
    const username = this.resolveUsername(row);
    const userRoles = this.resolveUserRoles(row, roleMap);
    const customerAssignmentIds = this.resolveCustomerAssignmentIds(row);
    const customerAssignmentId =
      customerAssignmentIds[0] ?? this.resolveCustomerAssignmentId(row);
    const customerName = this.resolveCustomerName(
      row,
      customerMap,
      customerAssignmentId,
      customerAssignmentIds
    );
    return {
      userId: Number(row.userId ?? row.id ?? 0),
      username,
      email: row.email ?? row.emailId ?? 'N/A',
      customerName,
      customerNames: this.resolveCustomerNames(row, customerMap, customerAssignmentIds),
      customerAssignmentId,
      customerAssignmentIds,
      userRoles,
      userRole: userRoles.length > 0 ? userRoles.join(', ') : 'N/A',
      status,
      createdBy: row.createdBy ?? 'N/A',
      createdAt: row.createdAt ?? 'N/A',
      updatedAt: row.updatedAt ?? 'N/A',
      customerId: customerAssignmentId,
      roleIds: this.resolveRoleIds(row),
      enableAdminAccess:
        row.enableAdminAccess ??
        row.isAdmin ??
        false,
    };
  }

  private resolveCustomerAssignmentIds(
    row: UserSearchRawRow
  ): Array<number | string> {
    const record = row as Record<string, unknown>;
    const rawList =
      row.customerAssignmentIds ??
      record['customerAssignmentIds'] ??
      record['customerIds'];

    if (Array.isArray(rawList)) {
      const ids = rawList
        .map((id) => {
          const n = Number(id);
          return Number.isFinite(n) && n > 0 ? n : null;
        })
        .filter((id): id is number => id != null);
      if (ids.length > 0) {
        return ids;
      }
    }

    const singular = this.resolveCustomerAssignmentId(row);
    return singular != null ? [singular] : [];
  }

  private resolveCustomerNames(
    row: UserSearchRawRow,
    customerMap: Map<number, string>,
    customerAssignmentIds: Array<number | string>
  ): string[] {
    if (Array.isArray(row.customerNames) && row.customerNames.length > 0) {
      return row.customerNames
        .map((name) => String(name ?? '').trim())
        .filter((name) => name.length > 0 && name !== 'N/A');
    }

    const names = customerAssignmentIds
      .map((id) => customerMap.get(Number(id)))
      .filter((name): name is string => !!name && name.trim().length > 0);

    if (names.length > 0) {
      return names;
    }

    const direct = String(row.customerName ?? row.companyName ?? '').trim();
    if (direct && direct !== 'N/A') {
      return direct
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    }

    return [];
  }

  private resolveCustomerAssignmentId(
    row: UserSearchRawRow
  ): number | string | undefined {
    const record = row as Record<string, unknown>;
    const raw =
      row.customerAssignmentId ??
      row.customerAssignment ??
      row.customerId ??
      record['customerAssignmentId'];

    if (raw == null || String(raw).trim() === '') {
      return undefined;
    }

    if (typeof raw === 'number' || typeof raw === 'string') {
      return raw;
    }

    return undefined;
  }

  private resolveCustomerName(
    row: UserSearchRawRow,
    customerMap: Map<number, string>,
    customerAssignmentId?: number | string,
    customerAssignmentIds: Array<number | string> = []
  ): string {
    const fromList = this.resolveCustomerNames(
      row,
      customerMap,
      customerAssignmentIds.length > 0
        ? customerAssignmentIds
        : customerAssignmentId != null
          ? [customerAssignmentId]
          : []
    );
    if (fromList.length > 0) {
      return fromList.join(', ');
    }

    const direct = String(row.customerName ?? row.companyName ?? '').trim();
    if (direct && direct !== 'N/A') {
      return direct;
    }

    const assignmentId = customerAssignmentId ?? this.resolveCustomerAssignmentId(row);
    if (assignmentId == null) {
      return 'N/A';
    }

    const mapped = customerMap.get(Number(assignmentId));
    if (mapped) {
      return mapped;
    }

    return 'N/A';
  }

  /**
   * BE does not provide a dedicated username column.
   * Build Username as "<firstName> <lastName>" with safe fallbacks.
   */
  private resolveUsername(row: UserSearchRawRow): string {
    const firstName = (row.firstName ?? row.firstname ?? '').trim();
    const lastName = (row.lastName ?? row.lastname ?? '').trim();
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) {
      return fullName;
    }

    return row.username ?? row.userName ?? 'N/A';
  }

  private resolveStatus(row: UserSearchRawRow): 'Active' | 'Inactive' | string {
    if (row.status == null || String(row.status).trim() === '') {
      return 'N/A';
    }

    const normalized = String(row.status).trim().toUpperCase();
    if (normalized === 'ACTIVE') return 'Active';
    if (normalized === 'INACTIVE') return 'Inactive';
    return String(row.status).trim();
  }

  private resolveUserRoles(
    row: UserSearchRawRow,
    roleMap: Map<number, string>
  ): string[] {
    const record = row as Record<string, unknown>;
    const fromArrays = this.extractRoleNamesFromArrays([
      row.roleNames,
      record['roleNameList'] as string[] | undefined,
      record['assignedRoleNames'] as string[] | undefined,
      record['userRoleNames'] as string[] | undefined,
      record['roleNamesList'] as string[] | undefined,
      row.userRoles,
      row.roles,
      row.roleList,
      row.assignedRoles,
      row.userRoleList,
      row.roleAssignments,
      record['userRoleAssignments'] as Array<Record<string, unknown>> | undefined,
      record['userRoleDtos'] as Array<Record<string, unknown>> | undefined,
      record['userRoleDtoList'] as Array<Record<string, unknown>> | undefined,
      record['roleDetails'] as Array<Record<string, unknown>> | undefined,
      record['userManagementRoles'] as Array<Record<string, unknown>> | undefined,
    ]);
    if (fromArrays.length > 0) {
      return fromArrays;
    }

    const scanned = this.scanRecordForRoleNames(record);
    if (scanned.length > 0) {
      return scanned;
    }

    const nested = this.extractNestedRoleNames(record);
    if (nested.length > 0) {
      return nested;
    }

    const roleIds = this.resolveRoleIds(row);
    if (roleIds.length > 0 && roleMap.size > 0) {
      const fromMap = roleIds
        .map((id) => roleMap.get(id))
        .filter((name): name is string => !!name && name.trim().length > 0);
      if (fromMap.length > 0) {
        return fromMap;
      }
    }

    const legacyValue = row.userRole ?? row.roleName ?? record['role'];
    if (Array.isArray(legacyValue)) {
      return this.extractRoleNamesFromArrays([legacyValue as unknown[]]);
    }

    const legacy = String(legacyValue ?? '').trim();
    if (!legacy || legacy === 'N/A') {
      return [];
    }

    if (legacy.includes(',')) {
      return legacy
        .split(',')
        .map((name) => name.trim())
        .filter((name) => name.length > 0);
    }

    return [legacy];
  }

  private extractNestedRoleNames(record: Record<string, unknown>): string[] {
    const names: string[] = [];

    for (const value of Object.values(record)) {
      if (!Array.isArray(value)) {
        continue;
      }

      for (const item of value) {
        if (!item || typeof item !== 'object') {
          continue;
        }
        const entry = item as Record<string, unknown>;
        const nestedRole = entry['role'];
        if (nestedRole && typeof nestedRole === 'object') {
          const nestedName = this.extractRoleNameFromItem(nestedRole);
          if (nestedName) {
            names.push(nestedName);
          }
        }
        const directName = this.extractRoleNameFromItem(entry);
        if (directName) {
          names.push(directName);
        }
      }
    }

    return [...new Set(names)];
  }

  private scanRecordForRoleNames(record: Record<string, unknown>): string[] {
    const names: string[] = [];

    for (const [key, value] of Object.entries(record)) {
      if (!/role/i.test(key)) {
        continue;
      }
      if (key === 'roleIds' || key === 'roleId' || key === 'userRoleIds') {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          const name = this.extractRoleNameFromItem(item);
          if (name) {
            names.push(name);
          }
        }
        continue;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed === 'N/A') {
          continue;
        }

        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed) as unknown;
            if (Array.isArray(parsed)) {
              names.push(...this.extractRoleNamesFromArrays([parsed]));
            } else if (parsed && typeof parsed === 'object') {
              const parsedName = this.extractRoleNameFromItem(parsed);
              if (parsedName) {
                names.push(parsedName);
              }
            }
          } catch {
            // ignore invalid JSON
          }
          continue;
        }

        if (!/^\d+$/.test(trimmed)) {
          if (trimmed.includes(',')) {
            names.push(
              ...trimmed
                .split(',')
                .map((part) => part.trim())
                .filter((part) => part.length > 0)
            );
          } else {
            names.push(trimmed);
          }
        }
      }
    }

    return [...new Set(names)];
  }

  private extractRoleNamesFromArrays(
    sources: Array<unknown[] | undefined>
  ): string[] {
    const names: string[] = [];

    for (const source of sources) {
      if (!Array.isArray(source)) {
        continue;
      }

      for (const item of source) {
        const name = this.extractRoleNameFromItem(item);
        if (name) {
          names.push(name);
        }
      }
    }

    return [...new Set(names)];
  }

  private extractRoleNameFromItem(item: unknown): string | null {
    if (typeof item === 'string') {
      const name = item.trim();
      return name.length > 0 && name !== 'N/A' ? name : null;
    }

    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const name = String(
      record['roleName'] ??
        record['roleCode'] ??
        record['name'] ??
        record['label'] ??
        ''
    ).trim();

    return name.length > 0 ? name : null;
  }

  private resolveRoleIds(row: UserSearchRawRow): number[] {
    const record = row as Record<string, unknown>;
    const candidates: unknown[] = [
      row.roleIds,
      record['assignedRoleIds'],
      record['userRoleIds'],
    ];

    for (const candidate of candidates) {
      const ids = this.parseRoleIdList(candidate);
      if (ids.length > 0) {
        return ids;
      }
    }

    const singularRoleId = record['roleId'];
    if (singularRoleId != null && singularRoleId !== '') {
      const id = Number(singularRoleId);
      if (Number.isFinite(id) && id > 0) {
        return [id];
      }
    }

    if (Array.isArray(row.roles)) {
      return row.roles
        .map((r) => Number(r?.roleId ?? r?.id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }

    const arraySources = [
      row.userRoles,
      row.roleList,
      row.assignedRoles,
      row.userRoleList,
      row.roleAssignments,
      record['userRoleAssignments'] as Array<Record<string, unknown>> | undefined,
    ];
    for (const source of arraySources) {
      if (!Array.isArray(source)) {
        continue;
      }
      const ids = source
        .map((item) => {
          if (typeof item === 'number' || typeof item === 'string') {
            return Number(item);
          }
          if (item && typeof item === 'object') {
            const rec = item as Record<string, unknown>;
            return Number(rec['roleId'] ?? rec['id']);
          }
          return NaN;
        })
        .filter((id) => Number.isFinite(id) && id > 0);
      if (ids.length > 0) {
        return ids;
      }
    }

    return [];
  }

  private parseRoleIdList(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }
      return trimmed
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id) && id > 0);
    }

    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return [value];
    }

    return [];
  }
}
